import React, { useState, useEffect } from 'react';
import { Image, Tooltip, DatePicker, TimePicker, Upload, Button, Space, message } from 'antd';
import type { UploadProps } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as AntdIcons from '@ant-design/icons';
import {
  getDictOptions,
  findDictOptionByValue,
  upload as coreUpload,
  getAccessToken,
  getCoreConfig,
} from '../../config';
import AndInput from '../AndInput';
import AndInputNumber from '../AndInputNumber';
import AndSelect from '../AndSelect';
import type { DictOption } from '../../config';
import type { ExtendedColumnType } from './types';
import { columnDataIndexToPathString } from '../../utils/normalizeColumnDataIndex';

export type ApplyColumnRenderOptions = {
  runAction?: (actionName: string, arg: unknown) => void;
  onCellValueChange?: (record: any, dataIndex: string, value: unknown) => void;
};

/**
 * 把 col.ellipsis 规范成布尔（接受 true/false/"true"/"false"）
 */
function normalizeEllipsis(col: ExtendedColumnType): boolean {
  const v = (col as any).ellipsis;
  return v === true || v === 'true';
}

/**
 * 从列上解析像素宽度：number | "300" | "300px" 都接受；其它（null / "" / undefined）返回 undefined。
 */
function resolveColumnWidth(col: ExtendedColumnType): number | undefined {
  const w = (col as any).width;
  if (typeof w === 'number' && Number.isFinite(w) && w > 0) return w;
  if (typeof w === 'string') {
    const trimmed = w.trim();
    if (!trimmed) return undefined;
    const n = Number(trimmed.replace(/px$/i, ''));
    if (Number.isFinite(n) && n > 0) return n;
  }
  return undefined;
}

/** 兜底把 raw value 转字符串作为 Tooltip 文案 */
function coerceTextString(text: any): string {
  if (text === null || text === undefined) return '';
  if (typeof text === 'string') return text;
  if (typeof text === 'number' || typeof text === 'boolean') return String(text);
  try {
    return String(text);
  } catch {
    return '';
  }
}

/** 把 date 的最终展示文本算出来，与 renderDateCell 的格式逻辑保持一致 */
function formatDateText(text: any, col: ExtendedColumnType): string {
  try {
    const parsed = dayjs(text);
    if (!parsed.isValid()) return '';
    if (col.dateFormat === 'date') return parsed.format('YYYY-MM-DD');
    if (col.dateFormat === 'time') return parsed.format('HH:mm:ss');
    return parsed.format('YYYY-MM-DD HH:mm:ss');
  } catch {
    return '';
  }
}

/** 把 money 的最终展示文本算出来，与 renderMoneyCell 的格式逻辑保持一致 */
function formatMoneyText(text: any, col: ExtendedColumnType): string {
  let v = parseFloat(text);
  if (isNaN(v)) return '无效金额';
  const minDec = col.minDecimalPlace || 0;
  const maxDec = col.maxDecimalPlace || 2;
  if (Number.isInteger(v) && minDec > 0) v = parseFloat(v.toFixed(minDec));
  let s = new Intl.NumberFormat('zh-CN', {
    style: 'decimal',
    minimumFractionDigits: minDec,
    maximumFractionDigits: maxDec,
    useGrouping: col.showCommaSeparator || false,
  }).format(v);
  if (col.currencySymbol) s = col.currencySymbol + s;
  return s;
}

/** 把 array 的最终展示文本算出来，与 renderArrayCell 的格式逻辑保持一致 */
function formatArrayText(text: any, col: ExtendedColumnType): string {
  if (text === null || text === undefined || text === '') return '';
  const sep = col.arraySeparator || ',';
  if (Array.isArray(text)) return text.join(sep);
  return String(text);
}

const ELLIPSIS_WRAP_STYLE: React.CSSProperties = {
  display: 'block',
  width: '100%',
  maxWidth: '100%',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
};

/**
 * 自定义省略号包裹器：
 * - 内层 div 用 max-width:100% + overflow:hidden + text-overflow:ellipsis 强制截断
 * - 外层 Tooltip 显示完整文本；空文本不显示 Tooltip
 */
function wrapWithEllipsis(node: React.ReactNode, fullText: string): React.ReactNode {
  const content = <div style={ELLIPSIS_WRAP_STYLE}>{node}</div>;
  if (!fullText) return content;
  return (
    <Tooltip title={fullText} placement="topLeft">
      {content}
    </Tooltip>
  );
}

/** 按字典类型缓存 getDictOptions 的 Promise，同列多行共用一个请求 */
const dictOptionsCache = new Map<string, Promise<DictOption[]>>();

function getCachedDictOptions(dictCode: string): Promise<DictOption[]> {
  let p = dictOptionsCache.get(dictCode);
  if (!p) {
    p = getDictOptions(dictCode);
    dictOptionsCache.set(dictCode, p);
  }
  return p;
}

/**
 * 字典单元格：根据 columnDictType 拉取选项，将 value 转成 label 显示
 */
const DictCell: React.FC<{ dictCode: string; value: any }> = ({ dictCode, value }) => {
  const [options, setOptions] = useState<DictOption[] | null>(null);

  useEffect(() => {
    if (!dictCode) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    getCachedDictOptions(dictCode).then((list) => {
      if (!cancelled) setOptions(list);
    });
    return () => {
      cancelled = true;
    };
  }, [dictCode]);

  if (value === null || value === undefined || value === '') {
    return <span />;
  }
  if (!options) {
    return <span>{String(value)}</span>;
  }
  const option = findDictOptionByValue(options, value);
  return <span>{option?.label ?? String(value)}</span>;
};

/**
 * 渲染数据字典列：通过 getCoreConfig().getDictOptions(columnDictType) 取选项，将 value 转成 label
 */
export const renderDictCell = (text: any, col: ExtendedColumnType): React.ReactNode => {
  const dictCode = col.columnDictType || col.dictType || '';
  if (!dictCode) return <span>{text != null && text !== '' ? String(text) : ''}</span>;
  return <DictCell dictCode={dictCode} value={text} />;
};

/**
 * 渲染图片列
 */
export const renderImageCell = (text: any, col: ExtendedColumnType): React.ReactNode => {
  const [width, height] = (col.imageWidthHeight || '').split(/\s*[,，]\s*/);
  const imgWidth = width || '50';
  const imgHeight = height || '50';

  let imageList: string[] = [];
  if (Array.isArray(text)) {
    imageList = text.slice(0, 3);
  } else if (typeof text === 'string' && text) {
    imageList = [text];
  }

  if (imageList.length === 0) {
    return <span>无图片</span>;
  }

  if (imageList.length === 1) {
    return (
      <Image
        src={imageList[0]}
        alt="image"
        width={imgWidth}
        height={imgHeight}
        preview={{ src: imageList[0] }}
      />
    );
  }

  return (
    <Image.PreviewGroup>
      {imageList.map((src, index) => (
        <Image
          key={index}
          src={src}
          alt={`image-${index}`}
          width={imgWidth}
          height={imgHeight}
          style={{ marginRight: 8, marginBottom: 8 }}
        />
      ))}
    </Image.PreviewGroup>
  );
};

/**
 * 渲染金额列
 */
export const renderMoneyCell = (text: any, col: ExtendedColumnType): React.ReactNode => {
  let moneyValue = parseFloat(text);
  if (isNaN(moneyValue)) {
    return <span>无效金额</span>;
  }

  const currencySymbol = col.currencySymbol || '';
  const showCommaSeparator = col.showCommaSeparator || false;
  const minDecimalPlace = col.minDecimalPlace || 0;
  const maxDecimalPlace = col.maxDecimalPlace || 2;

  if (Number.isInteger(moneyValue) && minDecimalPlace > 0) {
    moneyValue = parseFloat(moneyValue.toFixed(minDecimalPlace));
  }

  const numberFormatOptions: Intl.NumberFormatOptions = {
    style: 'decimal',
    minimumFractionDigits: minDecimalPlace,
    maximumFractionDigits: maxDecimalPlace,
    useGrouping: showCommaSeparator,
  };

  let formattedMoney = new Intl.NumberFormat('zh-CN', numberFormatOptions).format(moneyValue);

  if (currencySymbol) {
    formattedMoney = currencySymbol + formattedMoney;
  }

  return <span>{formattedMoney}</span>;
};

/**
 * 渲染日期列
 */
export const renderDateCell = (text: any, col: ExtendedColumnType): React.ReactNode => {
  let formattedDate = '';

  try {
    const parsedDate = dayjs(text);
    if (parsedDate.isValid()) {
      if (col.dateFormat === 'date') {
        formattedDate = parsedDate.format('YYYY-MM-DD');
      } else if (col.dateFormat === 'time') {
        formattedDate = parsedDate.format('HH:mm:ss');
      } else if (col.dateFormat === 'datetime') {
        formattedDate = parsedDate.format('YYYY-MM-DD HH:mm:ss');
      } else {
        formattedDate = parsedDate.format('YYYY-MM-DD HH:mm:ss');
      }
    }
  } catch {
    formattedDate = '';
  }

  return <span>{formattedDate}</span>;
};

/**
 * 渲染数组列
 */
export const renderArrayCell = (text: any, col: ExtendedColumnType): React.ReactNode => {
  if (text === null || text === undefined || text === '') {
    return <span></span>;
  }

  const separator = col.arraySeparator || ',';

  if (Array.isArray(text)) {
    return <span>{text.join(separator)}</span>;
  }

  return <span>{String(text)}</span>;
};

function getColumnDataIndex(col: ExtendedColumnType): string {
  const fromCol = columnDataIndexToPathString(col.dataIndex);
  if (fromCol) return fromCol;
  if (typeof col.key === 'string' || typeof col.key === 'number') return String(col.key);
  return '';
}

const CELL_CUSTOM_PROPS_RESERVED = new Set([
  'value',
  'onChange',
  'defaultValue',
  'dataSet',
  'dataSetField',
  'sessionId',
  'id',
  'develop',
  'index',
  'changeAction',
  'blurAction',
  'pressEnterAction',
  'clearAction',
  'rules',
  'validateOnBlur',
  'validateOnChange',
  'enableDict',
  'dictType',
]);

const CELL_CUSTOM_PROPS_UPLOAD_RESERVED = new Set([
  ...CELL_CUSTOM_PROPS_RESERVED,
  'customRequest',
  'beforeUpload',
  'showUploadList',
]);

function parseCellCustomProps(col: ExtendedColumnType): Record<string, unknown> {
  const raw = col.cellCustomProps;
  if (raw === undefined || raw === null || raw === '') return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return {};
}

function pickCellCustomProps(
  col: ExtendedColumnType,
  extraReserved?: Set<string>,
): Record<string, unknown> {
  const reserved = extraReserved ?? CELL_CUSTOM_PROPS_RESERVED;
  const parsed = parseCellCustomProps(col);
  const out: Record<string, unknown> = {};
  Object.keys(parsed).forEach((key) => {
    if (!reserved.has(key)) out[key] = parsed[key];
  });
  return out;
}

/**
 * 渲染文本填报列
 */
export const renderInputCell = (
  text: any,
  col: ExtendedColumnType,
  record: any,
  options?: ApplyColumnRenderOptions,
): React.ReactNode => {
  const dataIndex = getColumnDataIndex(col);
  const customProps = pickCellCustomProps(col);
  return (
    <AndInput
      value={text ?? ''}
      onChange={(value) => {
        if (!dataIndex || !options?.onCellValueChange) return;
        options.onCellValueChange(record, dataIndex, value);
      }}
      {...customProps}
    />
  );
};

/**
 * 渲染数值填报列
 */
export const renderInputNumberCell = (
  text: any,
  col: ExtendedColumnType,
  record: any,
  options?: ApplyColumnRenderOptions,
): React.ReactNode => {
  const dataIndex = getColumnDataIndex(col);
  const numericValue =
    text === '' || text === null || text === undefined
      ? null
      : typeof text === 'number'
        ? text
        : Number(text);
  const safeValue = numericValue === null || Number.isNaN(numericValue) ? null : numericValue;
  const customProps = pickCellCustomProps(col);
  return (
    <AndInputNumber
      value={safeValue}
      onChange={(value) => {
        if (!dataIndex || !options?.onCellValueChange) return;
        options.onCellValueChange(record, dataIndex, value);
      }}
      {...customProps}
    />
  );
};

/** 把 col.dateFormat 归一为 (pickerType, valueFormat) */
function resolveDateInputFormat(col: ExtendedColumnType): {
  pickerType: 'date' | 'time' | 'datetime';
  valueFormat: string;
} {
  const f = col.dateFormat;
  if (f === 'time') return { pickerType: 'time', valueFormat: 'HH:mm:ss' };
  if (f === 'datetime') return { pickerType: 'datetime', valueFormat: 'YYYY-MM-DD HH:mm:ss' };
  return { pickerType: 'date', valueFormat: 'YYYY-MM-DD' };
}

/** cellCustomProps.picker=month 时 persist/parse 与 format 对齐（如 YYYY-MM） */
function resolveDateInputPersistFormat(col: ExtendedColumnType): string {
  const custom = pickCellCustomProps(col);
  if (custom.picker === 'month') {
    const fmt = custom.format;
    if (typeof fmt === 'string' && fmt.trim()) return fmt.trim();
    return 'YYYY-MM';
  }
  return resolveDateInputFormat(col).valueFormat;
}

/** 解析单元格文本到 Dayjs；支持 Dayjs / 字符串 / 时间戳；解析失败返回 null */
function parseDateInputValue(text: any, valueFormat: string): Dayjs | null {
  if (text === null || text === undefined || text === '') return null;
  if (dayjs.isDayjs(text)) return text.isValid() ? text : null;
  if (typeof text === 'string') {
    const strict = dayjs(text, valueFormat, true);
    if (strict.isValid()) return strict;
    const loose = dayjs(text);
    return loose.isValid() ? loose : null;
  }
  const parsed = dayjs(text);
  return parsed.isValid() ? parsed : null;
}

function parseDateInputValueForColumn(text: any, col: ExtendedColumnType): Dayjs | null {
  const persistFormat = resolveDateInputPersistFormat(col);
  const defaultFormat = resolveDateInputFormat(col).valueFormat;
  const formats =
    persistFormat === defaultFormat ? [persistFormat] : [persistFormat, defaultFormat];
  for (const fmt of formats) {
    const parsed = parseDateInputValue(text, fmt);
    if (parsed) return parsed;
  }
  return null;
}

/**
 * 渲染日期填报列：按 col.dateFormat 选 DatePicker / TimePicker / 带时分秒的 DatePicker
 * 回写为对应格式的字符串；与 renderInputCell 一样依赖 onCellValueChange 写回 store
 */
export const renderDateInputCell = (
  text: any,
  col: ExtendedColumnType,
  record: any,
  options?: ApplyColumnRenderOptions,
): React.ReactNode => {
  const dataIndex = getColumnDataIndex(col);
  const { pickerType, valueFormat } = resolveDateInputFormat(col);
  const persistFormat = resolveDateInputPersistFormat(col);
  const value = parseDateInputValueForColumn(text, col);

  const customProps = pickCellCustomProps(col);
  const isMonthPicker = customProps.picker === 'month';

  const handleChange = (next: Dayjs | null) => {
    if (!dataIndex || !options?.onCellValueChange) return;
    if (isMonthPicker) {
      if (!next?.isValid()) return;
      options.onCellValueChange(record, dataIndex, next.format(persistFormat));
      return;
    }
    const persist = next && next.isValid() ? next.format(persistFormat) : '';
    options.onCellValueChange(record, dataIndex, persist);
  };

  const { style: customStyle, ...dateRestProps } = customProps;
  const commonStyle: React.CSSProperties = {
    width: '100%',
    ...(customStyle && typeof customStyle === 'object' ? (customStyle as React.CSSProperties) : {}),
  };

  if (pickerType === 'time') {
    return (
      <TimePicker
        value={value}
        onChange={handleChange}
        format={valueFormat}
        style={commonStyle}
        {...dateRestProps}
      />
    );
  }
  if (pickerType === 'datetime') {
    return (
      <DatePicker
        value={value}
        onChange={handleChange}
        showTime
        format={valueFormat}
        style={commonStyle}
        {...dateRestProps}
      />
    );
  }
  return (
    <DatePicker
      value={value}
      onChange={handleChange}
      format={valueFormat}
      style={commonStyle}
      {...dateRestProps}
    />
  );
};

/** 把 cell 的原始 text 归一为 src 字符串数组（兼容字符串 / 数组 / 含 url 的对象数组） */
function coerceSrcArray(text: any): string[] {
  if (text == null || text === '') return [];
  if (Array.isArray(text)) {
    return text
      .map((item: any) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return item.response?.src ?? item.src ?? item.url ?? item.thumbUrl ?? '';
        }
        return '';
      })
      .filter((s: any) => typeof s === 'string' && s);
  }
  if (typeof text === 'string') return [text];
  return [];
}

/**
 * 渲染上传填报列：
 * - antd Upload 最简模式：showUploadList=false；按 maxCount 控制上传按钮显隐
 * - customRequest 复用 coreUpload；onChange 拿到 done 时写回 src 数组到 onCellValueChange
 * - 已有上传项时显示一个紧凑的「预览」入口；点击通过 runAction 派发命令交业务侧处理
 */
export const renderUploadInputCell = (
  text: any,
  col: ExtendedColumnType,
  record: any,
  options?: ApplyColumnRenderOptions,
): React.ReactNode => {
  const dataIndex = getColumnDataIndex(col);
  const srcArray = coerceSrcArray(text);
  const maxCount = Math.max(1, Number(col.uploadMaxCount) || 1);
  const maxSize = Math.max(1, Number(col.uploadMaxSize) || 10);
  const previewAction = (col.uploadPreviewAction || '').trim();
  const buttonText = col.uploadButtonText || '上传';
  const reachMax = srcArray.length >= maxCount;

  const beforeUpload = (file: File): boolean => {
    const sizeMB = file.size / 1024 / 1024;
    if (sizeMB >= maxSize) {
      message.error(`文件大小不能超过 ${maxSize}MB`);
      return false;
    }
    return true;
  };

  const customRequest: UploadProps['customRequest'] = async (opts) => {
    const { file, onSuccess, onError, onProgress } = opts as any;
    const config = getCoreConfig();
    if (!config.upload) {
      onError?.(new Error('Upload handler not configured'));
      message.warning('上传功能未配置');
      return;
    }
    try {
      onProgress?.({ percent: 30 });
      const token = getAccessToken();
      const result = await coreUpload(file as File, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      onProgress?.({ percent: 100 });
      onSuccess?.(result, file);
    } catch (err) {
      onError?.(err as Error);
      message.error('上传失败');
    }
  };

  const handleChange: UploadProps['onChange'] = (info) => {
    if (!info?.file || info.file.status !== 'done') return;
    if (!dataIndex || !options?.onCellValueChange) return;
    const newSrc = (info.file.response as any)?.src;
    if (typeof newSrc !== 'string' || !newSrc) return;
    const next = [...srcArray, newSrc].slice(0, maxCount);
    options.onCellValueChange(record, dataIndex, next);
  };

  const handlePreviewClick = () => {
    if (!previewAction || !options?.runAction || srcArray.length === 0) return;
    options.runAction(previewAction, {
      record: { ...record },
      srcs: [...srcArray],
      src: srcArray[0],
      dataIndex,
    });
  };

  const handleDeleteClick = () => {
    if (!dataIndex || !options?.onCellValueChange) return;
    options.onCellValueChange(record, dataIndex, []);
  };

  const previewLabel = srcArray.length > 1 ? `预览(${srcArray.length})` : '预览';
  const customProps = pickCellCustomProps(col, CELL_CUSTOM_PROPS_UPLOAD_RESERVED);
  const { accept: customAccept, ...uploadRestProps } = customProps;

  return (
    <Space size={4}>
      {previewAction && srcArray.length > 0 && (
        <a onClick={handlePreviewClick}>{previewLabel}</a>
      )}
      {srcArray.length > 0 && (
        <a onClick={handleDeleteClick} style={{ color: '#ff4d4f' }}>删除</a>
      )}
      {!reachMax && (
        <Upload
          showUploadList={false}
          accept={col.uploadAccept || (typeof customAccept === 'string' ? customAccept : undefined)}
          beforeUpload={beforeUpload}
          customRequest={customRequest}
          onChange={handleChange}
          {...uploadRestProps}
        >
          <Button size="small" icon={<UploadOutlined />}>
            {buttonText}
          </Button>
        </Upload>
      )}
    </Space>
  );
};

/**
 * 渲染选择填报列：复用 AndSelect 的「自定义 onChange」分支
 * - 不绑 dataSet/dataSetField，不维护 _selected，不发 changeAction
 * - value 直接由 cell 的 text 提供；onChange 通过 onCellValueChange 写回所在行
 * - 字典通过列上的 columnDictType 启用（与「数据字典」展示列共用同一字段）
 */
export const renderSelectInputCell = (
  text: any,
  col: ExtendedColumnType,
  record: any,
  options?: ApplyColumnRenderOptions,
): React.ReactNode => {
  const dataIndex = getColumnDataIndex(col);
  const dictCode = col.columnDictType || col.dictType || '';
  const value = text === '' || text === null || text === undefined ? undefined : text;
  const customProps = pickCellCustomProps(col);

  return (
    <AndSelect
      enableDict={!!dictCode}
      dictType={dictCode || undefined}
      value={value}
      onChange={(next: any) => {
        if (!dataIndex || !options?.onCellValueChange) return;
        options.onCellValueChange(record, dataIndex, next);
      }}
      {...customProps}
    />
  );
};

function parseIconSizePx(raw: ExtendedColumnType['iconSize']): [number, number] {
  const fallback: [number, number] = [16, 16];
  if (raw == null || raw === '') return fallback;
  let arr: unknown[] = [];
  if (Array.isArray(raw)) {
    arr = raw;
  } else if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) arr = p;
      else return fallback;
    } catch {
      return fallback;
    }
  } else {
    return fallback;
  }
  const w = Number(arr[0]);
  const h = Number(arr[1]);
  if (!Number.isFinite(w) || !Number.isFinite(h)) return fallback;
  return [Math.max(1, w), Math.max(1, h)];
}

function isIconColumnValueEmpty(text: any): boolean {
  if (text === null || text === undefined || text === '') return true;
  if (Array.isArray(text) && text.length === 0) return true;
  return false;
}

/**
 * 渲染图标列：列配置固定图标；行字段为空时不展示；可配置点击派发事件
 */
export const renderIconCell = (
  text: any,
  col: ExtendedColumnType,
  record: any,
  options?: ApplyColumnRenderOptions,
): React.ReactNode => {
  if (isIconColumnValueEmpty(text)) {
    return <span />;
  }

  const iconName = col.columnIcon?.trim();
  if (!iconName) {
    return <span />;
  }

  const IconComp = (AntdIcons as unknown as Record<string, React.ElementType>)[iconName];
  if (!IconComp) {
    return <span />;
  }

  const [w, h] = parseIconSizePx(col.iconSize);
  const action = (col.iconClickAction || '').trim();
  const runAction = options?.runAction;

  const iconEl = (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: w,
        height: h,
      }}
    >
      <IconComp style={{ fontSize: Math.min(w, h) }} />
    </span>
  );

  if (action && runAction) {
    return (
      <span
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer', display: 'flex' }}
        onClick={() => runAction(action, { ...record })}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            runAction(action, { ...record });
          }
        }}
      >
        {iconEl}
      </span>
    );
  }

  return iconEl;
};

/**
 * 应用列格式化渲染：
 * - 列宽硬约束：只要 col.width 解析出像素宽度，就给 onCell/onHeaderCell 注入 max-width、
 *   min-width 与 width，避免分表模式下 header/body colgroup 不一致导致错列。
 * - 自定义 ellipsis：勾选 ellipsis 的文本类列由我们自己包裹截断 + Tooltip，最后关闭 antd 原生 ellipsis。
 */
export const applyColumnRendering = (
  col: ExtendedColumnType,
  options?: ApplyColumnRenderOptions,
): ExtendedColumnType => {
  const processedCol = { ...col };

  const ellipsisOn = normalizeEllipsis(processedCol);
  const resolvedWidth = resolveColumnWidth(processedCol);

  if (resolvedWidth !== undefined) {
    const userOnCell = (processedCol as any).onCell as
      | ((record: any, rowIndex?: number) => Record<string, any> | undefined)
      | undefined;
    const userOnHeaderCell = (processedCol as any).onHeaderCell as
      | ((column: any, index?: number) => Record<string, any> | undefined)
      | undefined;
    const widthStyle: React.CSSProperties = {
      width: resolvedWidth,
      minWidth: resolvedWidth,
      maxWidth: resolvedWidth,
      overflow: 'hidden',
      boxSizing: 'border-box',
    };

    (processedCol as any).onCell = (record: any, rowIndex?: number) => {
      const prev = (userOnCell ? userOnCell(record, rowIndex) : undefined) || {};
      return {
        ...prev,
        style: { ...(prev.style || {}), ...widthStyle },
      };
    };
    (processedCol as any).onHeaderCell = (column: any, index?: number) => {
      const prev = (userOnHeaderCell ? userOnHeaderCell(column, index) : undefined) || {};
      return {
        ...prev,
        style: { ...(prev.style || {}), ...widthStyle },
      };
    };
  }

  if (processedCol.specialFields === true && processedCol.renderType) {
    processedCol.render = (text: any, record: any, _index: number) => {
      switch (processedCol.renderType) {
        case 'image':
          return renderImageCell(text, processedCol);
        case 'money': {
          const node = renderMoneyCell(text, processedCol);
          return ellipsisOn ? wrapWithEllipsis(node, formatMoneyText(text, processedCol)) : node;
        }
        case 'date': {
          const node = renderDateCell(text, processedCol);
          return ellipsisOn ? wrapWithEllipsis(node, formatDateText(text, processedCol)) : node;
        }
        case 'array': {
          const node = renderArrayCell(text, processedCol);
          return ellipsisOn ? wrapWithEllipsis(node, formatArrayText(text, processedCol)) : node;
        }
        case 'dict': {
          const node = renderDictCell(text, processedCol);
          return ellipsisOn ? wrapWithEllipsis(node, coerceTextString(text)) : node;
        }
        case 'icon':
          return renderIconCell(text, processedCol, record, options);
        case 'input':
          return renderInputCell(text, processedCol, record, options);
        case 'inputNumber':
          return renderInputNumberCell(text, processedCol, record, options);
        case 'dateInput':
          return renderDateInputCell(text, processedCol, record, options);
        case 'uploadInput':
          return renderUploadInputCell(text, processedCol, record, options);
        case 'selectInput':
          return renderSelectInputCell(text, processedCol, record, options);
        default:
          return text;
      }
    };
  } else if (ellipsisOn) {
    const userRender = processedCol.render;
    processedCol.render = (text: any, record: any, index: number) => {
      const node = userRender ? userRender(text, record, index) : (text ?? '');
      return wrapWithEllipsis(node, coerceTextString(text));
    };
  }

  if (ellipsisOn) {
    (processedCol as any).ellipsis = false;
  }

  return processedCol;
};
